import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiButton, EuiButtonIcon, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiFormRow, EuiSpacer, EuiTextArea, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { IntervalUnit } from 'api/interfaces'
import { useGetAIPlanStubQuery, useEditAIPlanStubCampaignBuilderMutation } from 'api/rtkQueryApi/platform/aiPlansApi'

export interface EditAIPlanCampaignAudienceFlyoutProps {
  campaignBuilderId: string
  onClose: () => void
}

const EditAIPlanCampaignAudienceFlyout: React.FC<EditAIPlanCampaignAudienceFlyoutProps> = ({ campaignBuilderId, onClose }) => {
  const { aiPlanId } = useParams<{ aiPlanId: string }>()
  const { data: stub } = useGetAIPlanStubQuery({ aiPlanId }, { skip: !aiPlanId })
  const didFireUpdate = useRef(false)

  const builder = stub?.campaignBuilders.find(b => b.id === campaignBuilderId)

  const [audienceSummary, setAudienceSummary] = useState(builder?.audienceSummary || '')
  const [segmentDescriptions, setSegmentDescriptions] = useState<string[]>(builder?.segmentDescriptions || [])
  const [newDescription, setNewDescription] = useState('')

  const [editCampaignBuilder, { isSuccess, isLoading }] = useEditAIPlanStubCampaignBuilderMutation()

  useEffect(() => {
    if (didFireUpdate.current && isSuccess) {
      didFireUpdate.current = false
      onClose()
    }
  }, [didFireUpdate, isSuccess, onClose])

  if (!builder || !stub) return null

  const onAddDescription = () => {
    if (newDescription.trim()) {
      setSegmentDescriptions([...segmentDescriptions, newDescription.trim()])
      setNewDescription('')
    }
  }

  const onRemoveDescription = (index: number) => {
    setSegmentDescriptions(segmentDescriptions.filter((_, i) => i !== index))
  }

  const onUpdateDescription = (index: number, value: string) => {
    const updated = [...segmentDescriptions]
    updated[index] = value
    setSegmentDescriptions(updated)
  }

  const onSave = () => {
    const request = {
      name: builder.name,
      shortSummary: builder.shortSummary,
      longSummary: builder.longSummary,
      audienceSummary,
      audienceDetails: builder.audienceDetails || '',
      geoSummary: builder.geoSummary,
      geoDetails: builder.geoDetails || '',
      seasonalitySummary: builder.seasonalitySummary,
      seasonalityDetails: builder.seasonalityDetails || '',
      campaignType: builder.campaignType as unknown as string,
      isOffAdCritter: builder.isOffAdCritter,
      offAdCritterName: builder.offAdCritterName,
      offAdCritterColor: builder.offAdCritterColor,
      intervalBudget: builder.intervalBudget,
      retargetSite: builder.retargetSite,
      isTotalBudget: builder.isTotalBudget,
      startDate: moment(builder.startDate).toISOString(),
      endDate: builder.endDate ? moment(builder.endDate).toISOString() : undefined,
      intervalUnit: (builder.intervalUnit || IntervalUnit.Month) as unknown as string,
      intervalFrequency: builder.intervalFrequency || 1,
      intervalDuration: builder.intervalDuration || 1,
      segmentTargets: builder.segmentTargets || [],
      segmentDescriptions,
      audienceSegmentLooksUp: builder.audienceSegmentLooksUp || [],
      targetingType: builder.targetingType as unknown as string | undefined,
      regions: builder.regions || [],
      dmas: builder.dmas || [],
      cities: builder.cities || [],
      zips: builder.zips || [],
      keywords: builder.keywords || [],
      keywordDescription: builder.keywordDescription || '',
      includeInProjections: builder.includeInProjections || false,
      includeInGeoHeatmap: builder.includeInGeoHeatmap || false
    }

    editCampaignBuilder({
      aiPlanStubId: stub.id,
      campaignBuilderId,
      aiPlanId,
      request
    })
    didFireUpdate.current = true
  }

  return (
    <AcFlyout onClose={onClose} size='m'>
      <EuiFlyoutHeader>
        <EuiTitle size='m'>
          <h2>Edit Audience</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <EuiFormRow fullWidth label='Audience Summary'>
          <EuiTextArea fullWidth value={audienceSummary} onChange={e => setAudienceSummary(e.target.value)} rows={6} />
        </EuiFormRow>

        <EuiSpacer size='m' />

        <EuiFormRow fullWidth label='Audience Descriptions'>
          <div>
            {segmentDescriptions.map((desc, index) => (
              <div key={index} style={{ marginBottom: '8px' }}>
                <EuiFlexGroup gutterSize='s' alignItems='center' responsive={false}>
                  <EuiFlexItem>
                    <EuiFieldText fullWidth value={desc} onChange={e => onUpdateDescription(index, e.target.value)} />
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButtonIcon iconType='trash' color='danger' onClick={() => onRemoveDescription(index)} aria-label='Remove description' />
                  </EuiFlexItem>
                </EuiFlexGroup>
              </div>
            ))}
            <EuiSpacer size='s' />
            <EuiFlexGroup gutterSize='s' alignItems='center' responsive={false}>
              <EuiFlexItem>
                <EuiFieldText fullWidth placeholder='Add audience description...' value={newDescription} onChange={e => setNewDescription(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') onAddDescription() }} />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton size='s' onClick={onAddDescription} isDisabled={!newDescription.trim()}>Add</EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </div>
        </EuiFormRow>
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <EuiFlexGroup direction='row' alignItems='center'>
          <EuiFlexItem grow={false}>
            <EuiButton onClick={onClose}>Cancel</EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton fill onClick={onSave} isLoading={isLoading}>Save</EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditAIPlanCampaignAudienceFlyout
